/**
 * `runSearch`: one query, every wired source run in parallel, folded into
 * the groups a panel draws -- agora#216's "one search field ... finds mail,
 * calendar events, chat messages and people, grouped".
 *
 * Mail, Calendar, Chat and Files are independent requests. Chat's own group
 * also gets room-name matches, folded in synchronously ahead of its message
 * hits (`chat.ts`'s `matchRooms`) -- so a channel still turns up even the
 * one time its own message search fails, since the rooms it would have
 * searched are already in memory (agora#268). People costs nothing extra
 * beyond that: built from whichever of Mail and Calendar came back
 * (`people.ts`), plus Chat's own company-space people, matched the same
 * synchronous way (`chat.ts`'s `chatPeopleRows`) -- so a chat-only agora
 * still gets a People group.
 *
 * A source that throws never blanks the others: `settle` below turns a
 * rejection into `{ ok: false, error }` rather than letting `Promise.all`
 * reject the whole call.
 */
import type { CalendarClient } from '@wtfalch/calendar/calendar';
import { CalendarError } from '@wtfalch/calendar/calendar';
import type { MailClient } from '@wtfalch/email/mailbox';
import { toMailError } from '@wtfalch/email/mailbox';

import type { FilesSource } from '../files/source.ts';
import { searchCalendarEvents } from './calendar.ts';
import type { ChatSearchClient } from './chat.ts';
import { chatPeopleRows, matchRooms, searchChat } from './chat.ts';
import { searchFiles } from './files.ts';
import { searchMail } from './mail.ts';
import { peopleRows } from './people.ts';
import type { SearchGroup, SearchRow } from './types.ts';

export type SearchClients = {
  mail?: MailClient | null;
  calendar?: CalendarClient | null;
  chat?: ChatSearchClient | null;
  files?: FilesSource | null;
};

/** People sits first: it is who you were looking for, not what they wrote.
 *  Mail, then Calendar, then Chat, then Files -- the order agora#216's
 *  2026-09-21 comment lists the first three in; Files joins after Chat as
 *  the fourth group added to the panel. */
const GROUP_ORDER: readonly { id: string; label: string }[] = [
  { id: 'people', label: 'People' },
  { id: 'mail', label: 'Mail' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'chat', label: 'Chat' },
  { id: 'files', label: 'Files' },
];

type Settled<T> = { ok: true; value: T } | { ok: false; error: string };

async function settle<T>(
  fn: () => Promise<T>,
  errorMessage: (err: unknown) => string,
): Promise<Settled<T>> {
  try {
    return { ok: true, value: await fn() };
  } catch (err) {
    return { ok: false, error: errorMessage(err) };
  }
}

/** Exported for the full-results views (`mail/MailSearchResults.tsx` and
 *  its Calendar and Chat counterparts, agora#269): the same one-line message
 *  a group's own error gets here, reused rather than re-derived. */
export function mailErrorMessage(err: unknown): string {
  return toMailError(err).message;
}

export function calendarErrorMessage(err: unknown): string {
  return err instanceof CalendarError ? err.message : 'Something went wrong reaching the calendar.';
}

/** `@wtfalch/chat` exports no error type of its own; `Chat.tsx`'s own
 *  `useChatRooms` reads the same `instanceof Error` off a rejected
 *  `ChatClient` call, for the same reason. */
export function chatErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong reaching chat.';
}

/** `FilesSource` (`../files/source.ts`) exports no error type of its own
 *  either -- same fallback as `chatErrorMessage` above. */
export function filesErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong reaching files.';
}

/** Newest first within a group -- rows with no `time` (nothing currently
 *  produces one, but a future source might) sort after every timed row
 *  rather than floating to the top by comparing as `NaN`. */
function byNewest(rows: readonly SearchRow[]): readonly SearchRow[] {
  return [...rows].sort((a, b) => {
    const at = a.time ? new Date(a.time).getTime() : Number.NEGATIVE_INFINITY;
    const bt = b.time ? new Date(b.time).getTime() : Number.NEGATIVE_INFINITY;
    return bt - at;
  });
}

export async function runSearch(
  agoraId: string,
  clients: SearchClients,
  query: string,
  signal: AbortSignal,
): Promise<readonly SearchGroup[]> {
  const trimmed = query.trim();
  if (!trimmed || signal.aborted) return [];

  const mailClient = clients.mail ?? null;
  const calendarClient = clients.calendar ?? null;
  const chatClient = clients.chat ?? null;
  const filesSource = clients.files ?? null;

  const [mailSettled, calendarSettled, chatSettled, filesSettled] = await Promise.all([
    mailClient ? settle(() => searchMail(mailClient, agoraId, trimmed), mailErrorMessage) : null,
    calendarClient
      ? settle(() => searchCalendarEvents(calendarClient, agoraId, trimmed), calendarErrorMessage)
      : null,
    chatClient ? settle(() => searchChat(chatClient, agoraId, trimmed), chatErrorMessage) : null,
    filesSource
      ? settle(() => searchFiles(filesSource, agoraId, trimmed, signal), filesErrorMessage)
      : null,
  ]);

  if (signal.aborted) return [];

  const rowsById: Record<string, SearchGroup['result'] | undefined> = {};

  if (mailSettled) {
    rowsById.mail = mailSettled.ok
      ? { ok: true, rows: byNewest(mailSettled.value.rows) }
      : { ok: false, error: mailSettled.error };
  }
  if (calendarSettled) {
    rowsById.calendar = calendarSettled.ok
      ? { ok: true, rows: byNewest(calendarSettled.value.rows) }
      : { ok: false, error: calendarSettled.error };
  }
  if (chatClient) {
    const roomRows = matchRooms(chatClient, agoraId, trimmed);
    if (chatSettled?.ok) {
      rowsById.chat = { ok: true, rows: [...roomRows, ...byNewest(chatSettled.value.rows)] };
    } else if (roomRows.length > 0) {
      // The message search failed but the rooms it would have searched are
      // already in memory -- shown as a plain success rather than losing
      // them behind the search's own error, which nobody could act on
      // anyway (see this file's own module doc).
      rowsById.chat = { ok: true, rows: roomRows };
    } else if (chatSettled) {
      rowsById.chat = { ok: false, error: chatSettled.error };
    }
  }
  if (filesSettled) {
    rowsById.files = filesSettled.ok
      ? { ok: true, rows: byNewest(filesSettled.value) }
      : { ok: false, error: filesSettled.error };
  }
  if (mailClient || calendarClient || chatClient) {
    const people = [
      ...peopleRows(
        agoraId,
        trimmed,
        mailSettled?.ok ? mailSettled.value.threads : [],
        calendarSettled?.ok ? calendarSettled.value.events : [],
      ),
      ...(chatClient ? chatPeopleRows(chatClient, agoraId, trimmed) : []),
    ];
    if (people.length > 0) rowsById.people = { ok: true, rows: people };
  }

  const groups: SearchGroup[] = [];
  for (const { id, label } of GROUP_ORDER) {
    const result = rowsById[id];
    if (!result) continue;
    // "A source with no search is left out rather than shown empty"
    // (agora#216): a group that succeeded with nothing to show is dropped,
    // one that failed keeps its one-line error so the failure is not silent.
    if (result.ok && result.rows.length === 0) continue;
    groups.push({ id, label, result });
  }
  return groups;
}
